/**
 * Zustand store for authentication state.
 *
 * CRITICAL: the access token MUST live in JS memory only. NEVER in
 * localStorage, sessionStorage, or IndexedDB (REQ-PRD-§11.2-A,
 * Guardrails). Refresh tokens are HttpOnly cookies managed by the
 * server — they never touch this store.
 *
 * No persistence middleware is wired. Reload clears the access token,
 * and the refresh endpoint is called to obtain a new one (introduced in
 * STORY-001-003). The `auth-store.test.ts` test asserts that calling
 * `setAccessToken` does not mutate `localStorage`.
 *
 * STORY-001-009 additive note:
 *   The api-client (`apps/web/src/lib/api-client.ts`) reads from this
 *   store on every request to populate `Authorization: Bearer …` and
 *   calls `clearAuth()` on persistent-401 paths. The `isAuthenticated`
 *   selector below is exposed for route guards (RequireAuth) so they
 *   don't subscribe to the whole token string.
 */
import { create } from "zustand";

export interface AuthState {
  accessToken: string | null;
  /**
   * Guest chat (SCRUM-273): the GUEST access token — a separate JWT audience
   * (`garden-guest`) the server accepts only on the guest-allowed routes. Kept
   * apart from `accessToken` so every member check (`selectIsAuthenticated`,
   * route guards, `useMe`) keeps meaning "a member is signed in". JS memory
   * only, exactly like the member token (REQ-PRD-§11.2-A).
   */
  guestToken: string | null;
  /**
   * Whether the one-shot boot-time silent refresh has completed (success
   * OR failure). Starts `false`. The access token lives in JS memory only,
   * so a hard reload starts with `accessToken: null` even when a valid
   * HttpOnly refresh cookie exists; `bootstrapAuth()` (api-client) attempts
   * a refresh on app boot and flips this to `true` when done. Route guards
   * wait for it so they don't redirect a still-logged-in user to /login
   * before the refresh resolves.
   */
  bootstrapped: boolean;
  setAccessToken: (token: string | null) => void;
  setBootstrapped: () => void;
  clearAuth: () => void;
  setGuestToken: (token: string | null) => void;
  clearGuest: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  guestToken: null,
  bootstrapped: false,
  setAccessToken: (token) => set({ accessToken: token }),
  setBootstrapped: () => set({ bootstrapped: true }),
  // clearAuth (logout / persistent 401) does NOT reset `bootstrapped`: the
  // boot attempt is still "done"; we just have no session, so guards redirect.
  clearAuth: () => set({ accessToken: null }),
  setGuestToken: (token) => set({ guestToken: token }),
  // Guest session over (claimed, discarded, expired, logged out).
  clearGuest: () => set({ guestToken: null }),
}));

/**
 * Selector helper for components that only care about the
 * authenticated/unauthenticated bit, not the token string itself.
 * Using it avoids spurious re-renders when the token rotates.
 */
export const selectIsAuthenticated = (state: AuthState): boolean =>
  state.accessToken !== null;

/** Selector for whether the boot-time silent refresh has completed. */
export const selectAuthBootstrapped = (state: AuthState): boolean =>
  state.bootstrapped;

/** A guest: no member token, a guest token in memory. A member is never a guest. */
export const selectIsGuest = (state: AuthState): boolean =>
  state.accessToken === null && state.guestToken !== null;

/** The guest token (for code that must attach it explicitly, e.g. the SSE stream). */
export const selectGuestToken = (state: AuthState): string | null => state.guestToken;
